import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { TABLE_CONTENT_TYPE, REPORT_TYPE_D3A } from 'src/commons/constants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';
import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { Report } from 'aws-sdk/clients/d3aservice';
import { Link } from 'src/components/common/Link';
import { createReportDetailLink } from 'src/routes';
import { StatusIcon } from 'src/components/permissions/myDatasets/statusIcon';
import { CommonPageProps } from '../common/CommonPageProps';

export interface ReportTableProps extends CommonPageProps {
  reports: Report[];
  nextToken?: string;
  loadMoreResults?: any;
  loading: boolean;
  handleRefresh: any;
  onSelectionChange?: (selectedItems: Report[]) => void;
  selectedReports?: Report[];
  onCompareReports?: () => void;
}

export const ReportTable = (props: ReportTableProps) => {
  const [tableMessage] = useState('No reports');
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const linkForReportId = (reportId: string) => {
    return <Link href={createReportDetailLink(reportId)}>{reportId}</Link>;
  };

  const statusIndicatorForStatus = (status: string) => {
    // ReportStatus values are: Running | Waiting | Ready | Failed.
    // We will use the common StatusIcon helper.
    return StatusIcon(status);
  };

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'id',
      header: 'Report ID',
      cell: (report: Report) => linkForReportId(report.reportId),
      width: 320,
      sortingField: 'reportId',
    },
    {
      id: 'name',
      header: 'Name',
      cell: (report: Report) => report.name,
      width: 250,
      sortingField: 'name',
    },
    {
      id: 'type',
      header: 'Report type',
      cell: (report: Report) => report.type || REPORT_TYPE_D3A,
      width: 150,
      sortingField: 'type',
    },
    {
      id: 'createdOn',
      header: 'Created on',
      cell: (report: Report) => report.createdOn,
      width: 200,
      sortingField: 'createdOn',
    },
    {
      id: 'reportStatus',
      header: 'Report status',
      cell: (report: Report) => statusIndicatorForStatus(report.status),
      width: 120,
      sortingField: 'status',
    },
    {
      id: 'accessProviderFilter',
      header: 'Access provider filter',
      cell: (report: Report) => report.config?.accessGrantFilter?.accessProvider ?? '-',
      width: 150,
    },
    {
      id: 'grantStatus',
      header: 'Access status filter',
      cell: (report: Report) => report.config?.accessGrantFilter?.status ?? '-',
      width: 200,
    },
    {
      id: 'governanceTagFilter',
      header: 'Governance tag filter',
      cell: (report: Report) => report.config?.accessGrantFilter?.dgsa ?? '-',
      width: 250,
    },
    {
      id: 'description',
      header: 'Description',
      cell: (report: Report) => report.description,
      width: 250,
      sortingField: 'description',
    },
  ];

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
  }, []);

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(props.reports, {
    filtering: {
      empty: <EmptyState title={tableMessage} subtitle={'No reports to display.'} />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {
      trackBy: 'reportId',
    },
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  // Override selection to persist across pagination
  const tableProps = {
    ...collectionProps,
    selectedItems: props.selectedReports || [],
    onSelectionChange: ({ detail }) => {
      props.onSelectionChange?.(detail.selectedItems);
    },
  };

  return (
    <Table
      {...tableProps}
      loadingText='Loading reports...'
      loading={props.loading}
      columnDefinitions={columnDefinitions}
      items={items}
      selectionType='multi'
      empty={<EmptyState title={'No reports'} subtitle={'There are no reports available.'} />}
      wrapLines={preferences.wrapLines}
      resizableColumns={true}
      header={
        <Header
          counter={'(' + props.reports.length + ')'}
          actions={
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant={'icon'} iconName={'refresh'} onClick={props.handleRefresh}>
                Refresh
              </Button>
              <Button
                variant={'normal'}
                disabled={!props.selectedReports || props.selectedReports.length !== 2}
                onClick={props.onCompareReports}
              >
                Compare reports
              </Button>
              {props.nextToken && props.nextToken.length > 0 && (
                <Button variant={'normal'} onClick={props.loadMoreResults}>
                  Load more results...
                </Button>
              )}
            </SpaceBetween>
          }
        >
          Reports
        </Header>
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      preferences={
        <CollectionPreferences
          title={'Preferences'}
          confirmLabel={'Confirm'}
          cancelLabel={'Cancel'}
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={largePageSizePreference}
          wrapLinesPreference={defaultWrapLinesPreference}
        />
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringAriaLabel='Filter reports'
          filteringPlaceholder='Find reports'
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
    />
  );
};
